// CombinedBarLine_WithLabels.jsx
import React, { useMemo, useState, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'

const DEFAULT_COLORS = [
    'rgba(63,39,255,1)',
    'rgba(255,99,132,1)',
    'rgba(54,162,235,1)',
    'rgba(255,159,64,1)',
    'rgba(75,192,192,1)',
    'rgba(153,102,255,1)'
]

function normalizeLabels(series) {
    for (const s of series || []) {
        if (s && Array.isArray(s.labels) && s.labels.length) return s.labels
    }
    const seen = new Set()
    const out = [];
    (series || []).forEach(s => {
        (s.labels || []).forEach(l => {
            if (!seen.has(l)) { seen.add(l); out.push(l) }
        })
    })
    return out
}

/*
 Plugin vẽ nhãn:
 - Với bar: vẽ số lượng phía trên đỉnh cột.
 - Với line: vẽ giá trị (thường là %) phía trên điểm.
 - Lấy giá trị trực tiếp từ dataset.data[index].
*/
const dataLabelPlugin = {
    id: 'dataLabelPlugin',
    afterDatasetsDraw: function (chart) {
        const ctx = chart.ctx
        ctx.save()
        const fontSize = 12
        ctx.font = `${fontSize}px sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'

        const datasets = chart.data && chart.data.datasets ? chart.data.datasets : []
        datasets.forEach((ds, dsIndex) => {
            // get meta and check dataset visibility (legend click sets meta.hidden)
            const meta = chart.getDatasetMeta(dsIndex)
            if (!meta || !meta.data) return
            if (meta.hidden === true) return // dataset hidden via legend
            if (ds.hidden === true) return   // dataset hidden programmatically

            const isBar = ds.type === 'bar' || (meta.type === 'bar')
            const isLine = ds.type === 'line' || (meta.type === 'line')

            meta.data.forEach((element, idx) => {
                if (!element) return
                // element may be hidden individually
                if (element.hidden) return
                if (element._model && element._model.hidden) return

                // value to display
                let raw = ds.data && typeof ds.data[idx] !== 'undefined' ? ds.data[idx] : null
                if (raw === null || raw === undefined) return

                // format
                let text = String(raw)
                if (ds.label && ds.label.includes('%')) {
                    const num = Number(raw)
                    if (!Number.isNaN(num)) text = `${num}%`
                }

                // position
                const model = element._model || {}
                const x = (typeof model.x === 'number') ? model.x : (element.x || (element.tooltipPosition && element.tooltipPosition().x) || 0)
                let y = (typeof model.y === 'number') ? model.y : (element.y || (element.tooltipPosition && element.tooltipPosition().y) || 0)

                if (isBar) {
                    // put label a bit above top of bar
                    const topY = (typeof model.y === 'number') ? model.y : y
                    y = topY - 6
                    ctx.textBaseline = 'bottom'
                } else if (isLine) {
                    y = y - 8
                    ctx.textBaseline = 'bottom'
                } else {
                    y = y - 6
                    ctx.textBaseline = 'bottom'
                }

                ctx.fillStyle = ds.borderColor || '#111'
                ctx.fillText(text, x, y)
            })
        })
        ctx.restore()
    }
}

export default function CombinedChart({ series = [], combineType = '' }) {
    const [mode, setMode] = useState(combineType || '') // 'both' | 'count' | 'percent'
    const labels = useMemo(() => normalizeLabels(series), [series])

    const seriesNames = Array.isArray(series) ? series.map(s => s.combinedName || '') : []

    useEffect(() => {
        setMode(combineType)
    }, [combineType])

    const fullDatasets = useMemo(() => {
        return (series || []).flatMap((s, idx) => {
            const map = {};
            (s.items || []).forEach(it => { if (it && it.label != null) map[it.label] = Number(it.value || 0) })
            const values = labels.map(l => (map[l] != null ? map[l] : 0))
            const total = (s.total != null) ? Number(s.total) : values.reduce((a, b) => a + b, 0)
            const percents = values.map(v => (total > 0 ? Number(((v / total) * 100).toFixed(2)) : 0))

            const color = DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
            const colorBg = color.replace(/1\)$/, '0.5)')

            const seriesLabel = seriesNames[idx] || `Series ${idx + 1}`

            const barDs = {
                type: 'bar',
                label: `${seriesLabel} (Số lượng)`,
                data: values,
                backgroundColor: colorBg,
                borderColor: color,
                borderWidth: 1,
                yAxisID: 'y-axis-count',
                categoryPercentage: 0.6,
                barPercentage: 0.9,
                _metaSeriesIndex: idx,
                _metaTotal: total,
                _metaName: seriesLabel
            }

            const lineDs = {
                type: 'line',
                label: `${seriesLabel} (%)`,
                data: percents,
                borderColor: color,
                backgroundColor: colorBg,
                pointBackgroundColor: color,
                pointBorderColor: '#fff',
                pointRadius: 4,
                fill: false,
                lineTension: 0.15,
                borderWidth: 2,
                yAxisID: 'y-axis-percent',
                order: 2,
                _metaSeriesIndex: idx,
                _metaTotal: total,
                _metaName: seriesLabel
            }

            return [barDs, lineDs]
        })
    }, [series, labels, seriesNames])

    const datasets = useMemo(() => {
        if (mode === 'both') return fullDatasets
        if (mode === 'count') return fullDatasets.filter((_, i) => i % 2 === 0)
        if (mode === 'percent') return fullDatasets.filter((_, i) => i % 2 === 1)
        return fullDatasets
    }, [fullDatasets, mode])

    const data = useMemo(() => ({ labels, datasets }), [labels, datasets])

    const yAxes = useMemo(() => {
        if (mode === 'count') {
            return [
                {
                    id: 'y-axis-count',
                    position: 'left',
                    ticks: { beginAtZero: true },
                    scaleLabel: { display: true, labelString: 'Số lượng lô' },
                    gridLines: { drawOnChartArea: true }
                }
            ]
        }
        if (mode === 'percent') {
            return [
                {
                    id: 'y-axis-percent',
                    position: 'left',
                    ticks: { beginAtZero: true, max: 100, callback: v => v + '%' },
                    scaleLabel: { display: true, labelString: '%' },
                    gridLines: { drawOnChartArea: true }
                }
            ]
        }
        return [
            {
                id: 'y-axis-percent',
                position: 'left',
                ticks: { beginAtZero: true, max: 100, callback: v => v + '%' },
                scaleLabel: { display: true, labelString: '%' }
            },
            {
                id: 'y-axis-count',
                position: 'right',
                ticks: { beginAtZero: true },
                gridLines: { drawOnChartArea: false },
                scaleLabel: { display: true, labelString: 'Số lượng lô' }
            }
        ]
    }, [mode])

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        scales: {
            xAxes: [{ ticks: { autoSkip: false }, gridLines: { display: false } }],
            yAxes
        },
        tooltips: {
            mode: 'index',
            intersect: false,
        },
        elements: { point: { hoverRadius: 6 } },
        hover: { mode: 'nearest', intersect: true }
    }), [yAxes])

    return (
        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                </div>
            </div>

            <div style={{ height: 600 }}>
                <Bar key={mode} data={data} options={options} plugins={[dataLabelPlugin]} />
            </div>
        </div>
    )
}
